import { Button } from '@/components/ui/button';
import { useAppRegion, useEnabledFeatures } from '@/hooks/dataHelpers/useCurrentUser';
import {
  getMacAppVersion,
  getMacSupportedVersionsKey,
  useOfflineAppDownloadUrl,
} from '@/lib/offline/OfflineAppHelper';
import { DownloadLinear } from '@assessprep/icons/solar';
import { useTranslate } from '@refinedev/core';
import {
  instructionsHeadingClass,
  instructionsListClass,
  sectionClass,
  supportedVersionsClass,
} from './sectionStyles';

export const MacTab = () => {
  const translate = useTranslate();
  const appRegion = useAppRegion();
  const enabledFeatures = useEnabledFeatures();

  const supportedVersionsKey = getMacSupportedVersionsKey(enabledFeatures);

  // Mac button model (APL-10011): the PRIMARY button always renders. The
  // Intel button appears only when a DISTINCT Intel installer exists (the
  // 13.0.0+ per-arch releases) — and only then does the primary button label
  // itself "(Apple Silicon)". Pre-13 orgs see one plain button, never two
  // buttons serving the identical binary.
  const macDownloadUrl = useOfflineAppDownloadUrl('mac', appRegion, enabledFeatures, 'silicon');
  const macIntelDownloadUrl = useOfflineAppDownloadUrl('mac', appRegion, enabledFeatures, 'intel');
  const showMacIntelButton = !!(macIntelDownloadUrl && macIntelDownloadUrl !== macDownloadUrl);

  return (
    <div className="space-y-6">
      <div className={sectionClass}>
        <div className="flex flex-wrap items-center gap-3">
          <Button asChild data-testid="download-mac-link">
            <a href={macDownloadUrl ?? undefined}>
              <DownloadLinear aria-hidden />
              {/* Arch label only when a distinct Intel build exists — a
                  single-artifact release keeps the classic plain label.
                  One interpolated key so RTL locales can reorder. */}
              {showMacIntelButton
                ? translate('OfflineApp.download_assessprep_mac_v_arch', {
                    version: getMacAppVersion(enabledFeatures),
                    arch: translate('OfflineApp.apple_silicon'),
                  })
                : translate('OfflineApp.download_assessprep_mac_v_version', {
                    version: getMacAppVersion(enabledFeatures),
                  })}
            </a>
          </Button>
          {showMacIntelButton && (
            <Button variant="outline" asChild data-testid="download-mac-intel-link">
              <a href={macIntelDownloadUrl}>
                <DownloadLinear aria-hidden />
                {translate('OfflineApp.download_assessprep_mac_v_arch', {
                  version: getMacAppVersion(enabledFeatures),
                  arch: translate('OfflineApp.intel'),
                })}
              </a>
            </Button>
          )}
        </div>
        <p className={supportedVersionsClass}>{translate(supportedVersionsKey)}</p>
      </div>
      <div className={sectionClass}>
        <h3 className={instructionsHeadingClass}>
          {translate('OfflineApp.instructions_to_install_the_app_on_mac')}
        </h3>
        <ol className={instructionsListClass}>
          <li>{translate('OfflineApp.you_can_take_a_test_by_using_our_assessprep_app')}</li>
          <li>{translate('OfflineApp.double_click_on_the_downloaded_dmg_file_and_follow_the')}</li>
        </ol>
      </div>
    </div>
  );
};
